/**
 * /well-servicing/invoices/ready ("Ready to invoice") — customer-approved field
 * tickets on no live invoice (GET /well-servicing/invoices/billable,
 * fieldiq-api#1295). Modelled on Pipeline & Facility's Ready to invoice and
 * Wireline's.
 *
 * ONE PANEL PER GROUP, in the API's order. A group is the set one invoice may
 * bill: one customer, one province or state, one currency. Each group offers
 * "Create invoice", which opens New invoice with that group's tickets ticked —
 * where the office sets the due date and notes and generates. The API groups
 * the tickets and sums each group's subtotal; nothing here does either.
 *
 * The API's action queue links "Ready to invoice" here
 * (fieldiq-api lib/action-queue/well-servicing.ts). Supervisors and
 * dispatchers read it; only admin and office staff are offered "Create
 * invoice" (the API refuses everyone else).
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Receipt } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { useWsBillable } from '../../../hooks/useWsInvoices';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { wsScopeText } from '../../../lib/ws-tickets';
import { canWriteWsInvoices, wsBillableGroupKey } from '../../../lib/ws-invoices';
import type { WsBillableGroup } from '../../../types/ws-invoices';
import { useBackToList, useOpenRecord } from '../../downhole/recordPage';
import { WS_EYEBROW, WS_PRIMARY, WsEmpty, WsLoading, WsPanel } from '../WsBits';
import { WS_INVOICES_PATH, WS_NEW_INVOICE_PATH } from './InvoicesScreen';

/** New invoice with one billable group's tickets ticked (this page's "Create invoice"). */
export const wsNewInvoiceForGroupPath = (g: Pick<WsBillableGroup, 'customerId' | 'provinceState' | 'currency'>) =>
  `${WS_NEW_INVOICE_PATH}?group=${encodeURIComponent(wsBillableGroupKey(g))}`;

const groupTitle = (g: WsBillableGroup) => g.customerName || '—';
const groupSub = (g: WsBillableGroup) => `${g.provinceState || 'Province or state not set'} · ${g.currency || '—'}`;

function ReadyGroup({ group, canWrite }: { group: WsBillableGroup; canWrite: boolean }) {
  const openRecord = useOpenRecord();
  return (
    <WsPanel
      eyebrow={groupSub(group)}
      title={groupTitle(group)}
      testId="ws-ready-group"
      actions={
        canWrite ? (
          <Link to={wsNewInvoiceForGroupPath(group)} className={WS_PRIMARY} data-testid="ws-ready-create">
            <Receipt className="size-4" aria-hidden="true" />
            Create invoice
          </Link>
        ) : undefined
      }
      footer={`${group.tickets.length} ticket${group.tickets.length === 1 ? '' : 's'} · ${fmtCurrency(group.subtotal, group.currency)} before tax`}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-left">
          <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr className="border-l-4 border-l-transparent">
              <th scope="col" className="px-4 py-3">Ticket</th>
              <th scope="col" className="px-4 py-3">Job</th>
              <th scope="col" className="px-4 py-3">Covers</th>
              <th scope="col" className="px-4 py-3">Approved by</th>
              <th scope="col" className="px-4 py-3">PO</th>
              <th scope="col" className="px-4 py-3 text-right">Subtotal</th>
            </tr>
          </thead>
          <tbody>
            {group.tickets.map((t) => (
              <tr key={t.ticketId} className="border-t border-l-4 border-fieldiq-border border-l-fi-green" data-testid="ws-ready-ticket">
                <td className="px-4 py-2.5">
                  <button
                    type="button"
                    className="font-mono text-sm font-extrabold text-white underline-offset-2 hover:underline"
                    onClick={() => openRecord(`/well-servicing/tickets/${t.ticketId}`)}
                  >
                    {t.ticketNumber || '—'}
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <Link to={`/well-servicing/jobs/${t.jobId}?tab=tickets`} className="font-mono text-sm text-fieldiq-text underline-offset-2 hover:underline">
                    {t.jobNumber || '—'}
                  </Link>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-sm text-fieldiq-text">{wsScopeText({ scope: t.scope, workDate: t.workDate })}</td>
                <td className="px-4 py-2.5 text-sm text-fieldiq-text">{t.customerApprovedByName || '—'}</td>
                <td className="px-4 py-2.5 font-mono text-sm text-fieldiq-text">{t.poNumber || '—'}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right font-mono text-sm tabular-nums text-white" data-testid="ws-ready-ticket-subtotal">{fmtCurrency(t.subtotal, group.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </WsPanel>
  );
}

export default function WsReadyToInvoiceScreen() {
  const back = useBackToList(WS_INVOICES_PATH);
  const { user } = useAuth();
  const canWrite = canWriteWsInvoices(user?.role);
  const billable = useWsBillable();
  const failure = queryLoadFailure(billable);
  const groups = useMemo(() => billable.data?.data ?? [], [billable.data]);
  // A tally of the loaded rows — the API returns no ticket count and the list
  // is unpaginated. Never money: every amount on this page is the API's.
  const ticketCount = groups.reduce((n, g) => n + g.tickets.length, 0);

  return (
    <div data-testid="ws-ready-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WS_EYEBROW}
        title="Ready to invoice"
        onBack={back}
        backLabel="Back to invoices"
        stats={[
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Tickets', sub: 'Approved, unbilled', tone: 'accent', loading: billable.isLoading, value: displayValue(billable.isLoading || failure ? undefined : ticketCount) },
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: 'Groups', sub: 'One invoice each', tone: 'neutral', loading: billable.isLoading, value: displayValue(billable.isLoading || failure ? undefined : groups.length) },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <p className="text-sm text-fieldiq-muted">
            Field tickets the customer has approved and no invoice bills yet, grouped the way one invoice may bill them: one customer, one province or state, one currency.
          </p>
          {!canWrite && groups.length > 0 && <p className="text-sm text-fieldiq-muted" data-testid="ws-ready-read-only">Only an admin or office staff can create an invoice.</p>}
          {billable.isLoading ? (
            <WsLoading label="Loading tickets ready to invoice" />
          ) : failure ? (
            <WsPanel eyebrow="Approved field tickets" title="Ready to invoice" testId="ws-ready-failed">
              <LoadFailureNotice error={queryFailureError(billable)} kind={failure} subject="tickets ready to invoice" />
            </WsPanel>
          ) : groups.length === 0 ? (
            <WsPanel eyebrow="Approved field tickets" title="Ready to invoice" testId="ws-ready-empty">
              <WsEmpty>No customer-approved field tickets are waiting to be invoiced.</WsEmpty>
            </WsPanel>
          ) : (
            groups.map((g) => <ReadyGroup key={wsBillableGroupKey(g)} group={g} canWrite={canWrite} />)
          )}
        </div>
      </section>
    </div>
  );
}
